/**
 * UserCollectionPage
 *
 * A single user-created collection ("User Collection" type), reached from the
 * My Collections group in the sidebar. Unlike the PR/RS collection pages, this
 * one is manageable: it shows the Add / Share / Rename / Delete action bar and
 * a per-row Remove column.
 *
 * Authorization is enforced by the backend (which rejects non-members); the
 * page is only reachable by members anyway, so no extra UI membership gate is
 * applied.
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Share2, Pencil, Trash2, MoreHorizontal } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import JournalArticlesShell, {
  redirectAwayFromForbiddenCollection,
} from "@/components/journal-articles/JournalArticlesShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  useJournalArticlesPage,
  stripLocationColumns,
} from "@/components/journal-articles/useJournalArticlesPage"
import { useResolvedCollection } from "@/components/journal-articles/useResolvedCollection"
import AddArticleToCollectionDropdown from "@/components/journal-articles/AddArticleToCollectionDropdown"
import ShareCollectionDialog, {
  ShareResult,
  DeleteCollectionDialog,
  RenameCollectionDialog,
} from "@/components/journal-articles/CollectionDialogs"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { sidebarOptionToPath } from "@/components/journal-articles/sidebarRoutes"
import { SidebarOption } from "@/components/journal-articles/JournalArticlesSidebar"
import { USER_COLLECTION_TYPE, removeSourcesFromCollection } from "@/services/biblioService"
import { JournalArticle } from "@/services/journalArticlesService"
import { useAuth } from "@/hooks/useAuth"

/** Title shown while the collection is resolving. */
const LOADING_TITLE: string = "Collection"

/** Short description shown under the collection name heading. */
const PAGE_DESCRIPTION: string = "Browse journal articles in one of your collections"

/**
 * The user collection page.
 *
 * @returns The rendered page element.
 */
export default function UserCollectionPage(): React.JSX.Element {
  const navigate = useNavigate()
  const resolved = useResolvedCollection(USER_COLLECTION_TYPE)

  useEffect(() => {
    if (resolved.status === "not-found") {
      redirectAwayFromForbiddenCollection(navigate)
    }
  }, [resolved.status, navigate])

  if (resolved.status === "ready") {
    return (
      <UserCollectionReady
        collectionId={resolved.collection.id}
        collectionName={resolved.collection.collectionName}
      />
    )
  }

  return (
    <JournalArticlesShell title={LOADING_TITLE}>
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardContent className="flex flex-1 flex-col min-h-0">
          <LoadingSpinner variant="outline" loadingContent="Loading collection..." />
        </CardContent>
      </Card>
    </JournalArticlesShell>
  )
}

/**
 * Props for the ready-state inner component.
 */
interface UserCollectionReadyProps {
  /** The resolved collection id. */
  collectionId: number
  /** The resolved collection name. */
  collectionName: string
}

/**
 * The ready-state body: a manageable table page scoped to the resolved
 * collection, with the Add / Share / Manage action bar, per-row remove, and the
 * rename / share / delete dialogs. Separated so the hook calls only run when the
 * collection is resolved (avoiding conditional hook issues).
 *
 * @param props - The resolved collection props.
 * @returns The rendered table page element.
 */
function UserCollectionReady({
  collectionId,
  collectionName,
}: UserCollectionReadyProps): React.JSX.Element {
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  const sidebarOption: SidebarOption = `collection-${collectionId}`
  const page = useJournalArticlesPage({ sidebarOption })

  // Management dialog visibility.
  const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false)
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  // The resolved collection record (for dialogs that need the full object).
  const collection = useMemo(
    () => page.collections.find((c) => c.id === collectionId) ?? null,
    [page.collections, collectionId]
  )

  /**
   * Navigate away from a now-inaccessible collection and refresh the shared
   * collections list. Called after delete or after losing access.
   */
  const leaveCollectionAndRefresh = useCallback(async (): Promise<void> => {
    page.clearSelectedArticle()
    navigate(sidebarOptionToPath("all-journal-articles"), { replace: true })
    await page.fetchCollections()
  }, [page, navigate])

  /**
   * Handle the result of a share/membership change: navigate away if the
   * collection was deleted or the user lost access, otherwise refresh names.
   *
   * @param result - The outcome reported by the share dialog.
   */
  const handleShareChanged = useCallback(
    async (result: ShareResult): Promise<void> => {
      if (result.deleted || result.lostOwnAccess) {
        await leaveCollectionAndRefresh()
        return
      }
      await page.fetchCollections()
    },
    [leaveCollectionAndRefresh, page]
  )

  /**
   * Remove an article from this collection, then refresh so the row disappears.
   *
   * @param article - The article to unlink.
   */
  const handleRemoveFromCollection = useCallback(
    async (article: JournalArticle): Promise<void> => {
      try {
        const token: string = await getAccessToken()
        await removeSourcesFromCollection({
          idToken: token,
          collectionId,
          sourceIds: [article.sourceId],
        })
        await page.refreshCurrentPage()
      } catch (err) {
        const message: string = err instanceof Error ? err.message : "Failed to remove article"
        toast.error(message, { duration: 5000 })
      }
    },
    [getAccessToken, collectionId, page]
  )

  /**
   * When the details panel updates an article such that it no longer belongs to
   * this collection, refresh so the row drops out.
   *
   * @param updated - The updated article from the panel.
   */
  const handleArticleUpdate = useCallback(
    (updated: JournalArticle): void => {
      if (!(updated.collectionIds ?? []).includes(collectionId)) {
        void page.refreshCurrentPage()
      }
    },
    [collectionId, page]
  )

  // The management action bar (Add / Share / Manage).
  const headerActions = (
    <>
      <AddArticleToCollectionDropdown
        collectionId={collectionId}
        collectionName={collectionName}
        // Best-effort exclusion from the loaded page; the backend add is
        // idempotent, so any already-linked source that slips through is
        // safely skipped.
        existingSourceIds={page.articles.map((article) => article.sourceId)}
        onArticleAdded={page.refreshCurrentPage}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShareDialogOpen(true)}
        aria-label={`Share collection ${collectionName}`}
      >
        <Share2 className="size-3.5" />
        Share
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label={`Manage collection ${collectionName}`}
          >
            <MoreHorizontal className="size-3.5" />
            Manage
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setRenameDialogOpen(true)}>
            <Pencil className="size-3.5" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onSelect={() => setDeleteDialogOpen(true)}>
            <Trash2 className="size-3.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )

  return (
    <JournalArticlesShell
      title={collectionName}
      description={PAGE_DESCRIPTION}
      headerActions={headerActions}
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
      showDetailsPanel
      onArticleUpdate={handleArticleUpdate}
    >
      <JournalArticlesTable
        page={page}
        filterOptions={filterOptions}
        visibleColumns={visibleColumns}
        onRemoveFromCollection={handleRemoveFromCollection}
      />

      {/* Management dialogs. */}
      <RenameCollectionDialog
        collection={renameDialogOpen ? collection : null}
        onClose={() => setRenameDialogOpen(false)}
        onRenamed={page.fetchCollections}
      />
      <ShareCollectionDialog
        collection={shareDialogOpen ? collection : null}
        onClose={() => setShareDialogOpen(false)}
        onChanged={handleShareChanged}
      />
      <DeleteCollectionDialog
        collection={deleteDialogOpen ? collection : null}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={leaveCollectionAndRefresh}
      />
    </JournalArticlesShell>
  )
}
